import { looksLikeCvars } from "@/games/destiny/parser";
import { looksLikeSC2Hotkeys } from "@/games/sc2/parser/index";
import type { BoardPartFormat, KeyboardContent } from "@/hardware/keyboards/keyboard-import";
import { type KLEData, lenientKLE } from "@/hardware/keyboards/kle-import";

/** The hotkey-file formats the pipeline can ingest into a Profile, one per Game with an export. */
export type ProfileFormat = "sc2-hotkeys" | "destiny-cvars";

/**
 * What the pipeline decided a piece of dropped or pasted content is. This is the import
 * dispatcher: it composes the keyboards' and the games' sniffers, so no module
 * has to know about the others. Board parts pair up via importKeyboardPair;
 * keyboard content goes straight to importKeyboard; profiles to importProfile.
 */
export type DetectedContent =
  | ({ kind: "keyboard" } & KeyboardContent)
  | { kind: "board-part"; format: BoardPartFormat; data: unknown; name: string | null }
  | { kind: "profile"; format: ProfileFormat; text: string }
  | { kind: "unknown"; reason: string };

/** Sniff content without the user choosing a type. Never throws. */
export function detectContent(text: string): DetectedContent {
  if (looksLikeSC2Hotkeys(text)) return { kind: "profile", format: "sc2-hotkeys", text };
  // a Destiny cvars.xml is XML, so it never collides with the JSON keyboard formats below; sniffed
  // before lenientKLE (which would reject it as not-JSON and report a misleading keyboard error).
  if (looksLikeCvars(text)) return { kind: "profile", format: "destiny-cvars", text };
  let parsed: unknown;
  try {
    parsed = lenientKLE(text);
  } catch {
    return { kind: "unknown", reason: "Expected KLE JSON, KLE raw data, or a VIA / QMK export." };
  }
  if (Array.isArray(parsed)) {
    // raw data for a single row parses as a flat array; treat it as one row only when it
    // carries actual key legends (strings), not just a metadata object or stray values
    const singleRow =
      !parsed.some(Array.isArray) && parsed.some((item) => typeof item === "string");
    const rows = (singleRow ? [parsed] : parsed) as KLEData;
    if (!rows.some((row) => Array.isArray(row) && row.length > 0)) {
      return { kind: "unknown", reason: "No key rows found in this KLE data." };
    }
    const metaRow = rows.find((row) => !Array.isArray(row)) as Record<string, unknown> | undefined;
    const name = typeof metaRow?.name === "string" ? metaRow.name : null;
    return { kind: "keyboard", format: "kle", rows, name };
  }
  if (parsed !== null && typeof parsed === "object") {
    const obj = parsed as Record<string, unknown>;
    const name = typeof obj.name === "string" ? obj.name : null;
    const layouts = obj.layouts as Record<string, unknown> | undefined;
    // VIA definition: KLE-style geometry rows under layouts.keymap plus a wired matrix
    if (Array.isArray(layouts?.keymap) && typeof obj.matrix === "object" && obj.matrix !== null) {
      return { kind: "board-part", format: "via-definition", data: obj, name };
    }
    // QMK info.json: named LAYOUT entries, each a flat array of key objects
    if (
      layouts &&
      !Array.isArray(layouts) &&
      Object.values(layouts).some((l) => Array.isArray((l as { layout?: unknown })?.layout))
    ) {
      const qmkName = typeof obj.keyboard_name === "string" ? obj.keyboard_name : name;
      return { kind: "board-part", format: "qmk-info", data: obj, name: qmkName };
    }
    if (Array.isArray(obj.layers)) {
      // a saved VIA keymap carries vendorProductId; a QMK Configurator export names keyboard + LAYOUT
      if ("vendorProductId" in obj) {
        return { kind: "board-part", format: "via-keymap", data: obj, name };
      }
      if (typeof obj.keyboard === "string" || typeof obj.layout === "string") {
        return { kind: "board-part", format: "qmk-keymap", data: obj, name };
      }
      return { kind: "unknown", reason: "This looks like a keymap, but not a VIA or QMK export." };
    }
  }
  return {
    kind: "unknown",
    reason: "Unrecognized JSON. Expected a KLE, VIA, or QMK keyboard file.",
  };
}
